import { wrapApiCall } from '@/lib/axios'
import type { ReportLang } from '@/utils/helper/reportLang'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import {
  CreateFacilityBlockedSlotPayload,
  CreateFacilityBlockedSlotResult,
  CreateFacilityBookingPayload,
  CreateFacilityPayload,
  CreateFacilityRentalItemPayload,
  CreateRecurringFacilityBookingPayload,
  FacilityAvailabilitySlot,
  FacilityBlockedSlot,
  FacilityBlockedSlotConflict,
  FacilityBookedListItem,
  FacilityBookingMembership,
  FacilityData,
  FacilityDataList,
  FacilityRentalItem,
  FacilitySummary,
  FacilityType,
  FindFacilityBookingMembershipParams,
  RecurringBookingPreviewSlot,
  RecurringBookingResult,
  SPORTS_FIELD_FACILITY_TYPE_ID,
  UpdateFacilityRentalItemPayload,
} from './model'

export const FacilitiesService = (client: AxiosInstance) => {
  // Raw seeded types exactly as the backend stores them — nothing hidden or relabeled. Facility
  // LIST rows carry `type` = this raw name, so matching rows by type name must use this list,
  // not the display list from getFacilityTypes (which hides "Swimming Pool" and relabels the
  // court type).
  const fetchFacilityTypesRaw = async (): Promise<FacilityType[]> => {
    const { data } = await wrapApiCall(() =>
      client.get<{ data?: FacilityType[] } | FacilityType[]>('/facility-types')
    )
    const result = Array.isArray(data) ? data : data?.data
    return result || []
  }

  return {
    async getFacilities(opt: PaginationOpt): Promise<PaginationResponse<FacilityDataList>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data?: Record<string, unknown>[]; pagination: Record<string, unknown> }>(
          '/facilities',
          { params: opt }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data:
          data?.data?.map((item: Record<string, unknown>) => deserialize(item, FacilityDataList)) ||
          [],
      }
    },
    async getFacilityTypesRaw(): Promise<FacilityType[]> {
      return fetchFacilityTypesRaw()
    },
    async getFacilityTypes(): Promise<FacilityType[]> {
      const result = await fetchFacilityTypesRaw()
      // Hidden from every type dropdown: seeded types not relevant to this complex, plus the
      // standalone "Swimming Pool" — pools are merged into Sports field (swimming = a sport).
      const HIDDEN_TYPES = new Set(['Physical Assessment', 'Training', 'Swimming Pool'])
      return (
        [...result]
          .filter((t) => !HIDDEN_TYPES.has(t.name))
          // Relabel the merged court/pool type (display only — list rows keep the raw name).
          .map((t) =>
            t.id === SPORTS_FIELD_FACILITY_TYPE_ID
              ? { ...t, name: 'Sports Field / Swimming Pool' }
              : t
          )
          // Keep seed order but always push "Others" to the bottom.
          .sort((a, b) => (a.name === 'Others' ? 1 : 0) - (b.name === 'Others' ? 1 : 0))
      )
    },
    async createFacilities(facilityPayload: CreateFacilityPayload): Promise<FacilityData> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/facilities', facilityPayload)
      )
      return deserialize(data, FacilityData)
    },
    async updateFacilities(
      id: string,
      facilityPayload: CreateFacilityPayload
    ): Promise<FacilityData> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/facilities/${id}`, facilityPayload)
      )
      return deserialize(data, FacilityData)
    },
    async facilitiesUploadImage(file: File): Promise<{ imageUrl: string }> {
      const formData = new FormData()
      formData.append('image', file)
      const { data } = await wrapApiCall(() =>
        client.post<{ imageUrl: string }>(`/facilities/image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      )
      return data
    },
    async getFacilitiesDetail(id: string): Promise<FacilityData> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/facilities/${id}`)
      )
      return deserialize(data, FacilityData)
    },
    async getFacilityAvailability(
      facilityId: string,
      params: { startTime: string; duration: number }
    ): Promise<FacilityAvailabilitySlot[]> {
      const { data } = await wrapApiCall(() =>
        client.get<FacilityAvailabilitySlot[] | { data?: FacilityAvailabilitySlot[] }>(
          `/facilities/${facilityId}/availability`,
          { params }
        )
      )
      const body = data
      const list = Array.isArray(body) ? body : body?.data
      return Array.isArray(list) ? list : []
    },
    async deleteFacilities(id: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/facilities/${id}`)
      )
      return !!data?.success
    },
    async cancelBooking(id: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/facilities/booking/${id}`)
      )
      return !!data?.success
    },
    async getFacilitiesBookedList(
      opt: PaginationOpt
    ): Promise<PaginationResponse<FacilityBookedListItem>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data?: Record<string, unknown>[]; pagination: Record<string, unknown> }>(
          'facility-bookings',
          { params: opt }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data:
          data?.data?.map((item: Record<string, unknown>) =>
            deserialize(item, FacilityBookedListItem)
          ) || [],
      }
    },
    async getFacilitySummary(params: {
      typeId?: string
      sport?: string
      start?: string
      end?: string
      /** Scope the summary to a single facility (per-facility usage section). */
      facilityId?: string
    }): Promise<FacilitySummary> {
      const { data } = await wrapApiCall(() =>
        client.get<FacilitySummary>('/facility-bookings/summary', { params })
      )
      return data
    },
    // Dormant: FE coach-filtering was descoped (see docs/facility-backend-spec.md P1.4);
    // the BE endpoint is kept intact for a possible future return. No current caller.
    async getEligibleInstructors(
      facilityId: string
    ): Promise<{ id: number; firstName: string; lastName: string; nickname: string | null }[]> {
      const { data } = await wrapApiCall(() =>
        client.get<{ id: number; firstName: string; lastName: string; nickname: string | null }[]>(
          '/facility-bookings/eligible-instructors',
          { params: { facilityId } }
        )
      )
      return data || []
    },
    async getFacilityBookingsById(id: string): Promise<FacilityBookedListItem> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/facility-bookings/${id}`)
      )

      return deserialize(data, FacilityBookedListItem)
    },
    /**
     * Mark a facility booking as no-show. NOTE: backend endpoint pending — proposed contract:
     * PATCH /facility-bookings/:id/no-show → sets status = NO_SHOW (and forfeits deposit per policy).
     */
    async markFacilityBookingNoShow(id: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(`/facility-bookings/${id}/no-show`)
      )
      return !!data?.success
    },
    /**
     * Manual deposit: staff record that the member has paid the deposit (cash/transfer).
     * PATCH /facility-bookings/:id/deposit-paid → depositStatus PENDING → PAID.
     */
    async markFacilityBookingDepositPaid(id: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(`/facility-bookings/${id}/deposit-paid`)
      )
      return !!data?.success
    },
    /** Staff confirms arrival (check-in). PATCH /facility-bookings/:id/check-in. */
    async markFacilityBookingCheckIn(id: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(`/facility-bookings/${id}/check-in`)
      )
      return !!data?.success
    },
    /**
     * Move a booking to a new time and/or court. Money (price/deposit/quota) is kept as agreed.
     * PATCH /facility-bookings/:id/reschedule.
     */
    async rescheduleFacilityBooking(
      id: string,
      payload: { startAt: string; endAt: string; facilityId?: string }
    ): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(`/facility-bookings/${id}/reschedule`, payload)
      )
      return !!data?.success
    },
    /**
     * Staff records a counter payment (no slip) — cash or credit card → marks the booking PAID +
     * issues a receipt, settling any pending deposit. PATCH /facility-bookings/:id/mark-paid-cash.
     */
    async markFacilityBookingPaidCash(
      id: string,
      method: 'CASH' | 'CARD' = 'CASH'
    ): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(`/facility-bookings/${id}/mark-paid-cash`, { method })
      )
      return !!data?.success
    },
    /**
     * The receipt issued for this booking's cash settlement — null when none exists
     * (quota/credit/free bookings, or ones settled before receipts covered facility
     * bookings). GET /facility-bookings/:id/receipt; used to print the standard PDF.
     */
    async getFacilityBookingReceipt(id: string): Promise<{ id: number; code: string } | null> {
      const { data } = await wrapApiCall(() =>
        client.get<{ receipt: { id: number; code: string } | null }>(
          `/facility-bookings/${id}/receipt`
        )
      )
      return data?.receipt ?? null
    },
    /**
     * Cancel a booking. `refundDeposit` is an optional staff override for the manual deposit
     * settlement (true = refund, false = forfeit). Omit to let the backend apply the
     * cancel-window policy (before cutoff → refund, after → forfeit).
     */
    async deleteFacilityBooking(
      id: string,
      refundDeposit?: boolean,
      opts?: { branchIdOverride?: number }
    ): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/facility-bookings/${id}`, {
          ...(refundDeposit === undefined
            ? {}
            : { params: { refundDeposit: refundDeposit ? 'true' : 'false' } }),
          ...(opts?.branchIdOverride != null
            ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
            : {}),
        })
      )
      return !!data?.success
    },
    async createFacilityBooking(
      payload: CreateFacilityBookingPayload
    ): Promise<FacilityBookedListItem> {
      const body = {
        ...payload,
        facilityId: String(payload.facilityId),
      }
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/facility-bookings', body)
      )
      return deserialize(data, FacilityBookedListItem)
    },
    /**
     * Weekly recurring series (regular group): repeats the base booking on the same weekday +
     * time for `weeks` consecutive weeks under one recurringGroupId.
     * POST /facility-bookings/recurring.
     */
    async createRecurringFacilityBooking(
      payload: CreateRecurringFacilityBookingPayload
    ): Promise<RecurringBookingResult> {
      const body = {
        ...payload,
        facilityId: String(payload.facilityId),
      }
      const { data } = await wrapApiCall(() =>
        client.post<RecurringBookingResult>('/facility-bookings/recurring', body)
      )
      return data
    },
    /**
     * Availability dry-run for a recurring series — one slot per week, no booking created.
     * POST /facility-bookings/recurring/preview.
     */
    async previewRecurringFacilityBooking(payload: {
      facilityId: string
      startAt: string
      endAt: string
      endDate: string
      daysOfWeek?: number[]
      /** Series headcount (QTY-2052): an occurrence is available only if this many seats fit. */
      partySize?: number
    }): Promise<RecurringBookingPreviewSlot[]> {
      const { data } = await wrapApiCall(() =>
        client.post<RecurringBookingPreviewSlot[]>('/facility-bookings/recurring/preview', payload)
      )
      return Array.isArray(data) ? data : []
    },
    /**
     * Cancel every remaining (future, still-occupied) occurrence of a recurring series.
     * DELETE /facility-bookings/recurring/:groupId.
     */
    async cancelRecurringFacilityBookingSeries(
      recurringGroupId: string,
      refundDeposit?: boolean
    ): Promise<{ cancelled: number; failed: number }> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ cancelled: number; failed: number }>(
          `/facility-bookings/recurring/${recurringGroupId}`,
          {
            ...(refundDeposit === undefined
              ? {}
              : { params: { refundDeposit: refundDeposit ? 'true' : 'false' } }),
          }
        )
      )
      return data
    },
    async findFacilityBookingMembership(
      params: FindFacilityBookingMembershipParams
    ): Promise<FacilityBookingMembership | null> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>('/facility-bookings/find-membership', { params })
      )
      return deserialize(data, FacilityBookingMembership)
    },
    async updateFacilityBookingSlip(
      id: string,
      paymentSlipUrl: string,
      approve: boolean
    ): Promise<FacilityData> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/facility-bookings/${id}/slip`, {
          approve,
          paymentSlipUrl,
        })
      )
      return deserialize(data, FacilityData)
    },
    /**
     * Replace the equipment-rental line items on a booking (single bookings only). The server
     * recomputes rentalTotalPrice + totalPrice. Allowed only while paymentStatus != PAID and the
     * booking is not CANCELLED/NO_SHOW. PATCH /facility-bookings/:id/rental-items.
     */
    async updateFacilityBookingRentalItems(
      id: string,
      rentalItems: { rentalItemId: string; quantity: number }[]
    ): Promise<FacilityBookedListItem> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/facility-bookings/${id}/rental-items`, {
          rentalItems,
        })
      )
      return deserialize(data, FacilityBookedListItem)
    },
    /* ---------------------------------------------------------------------- */
    /* Blocked slots (section C)                                              */
    /* ---------------------------------------------------------------------- */
    /**
     * Close a court for a time range. Creation always succeeds; the response lists any active
     * bookings the block now overlaps (on the composite family) so staff can resolve them by hand
     * — blocks prevent NEW bookings, they do NOT auto-cancel existing ones.
     * POST /facility-blocked-slots.
     */
    async createFacilityBlockedSlot(
      payload: CreateFacilityBlockedSlotPayload
    ): Promise<CreateFacilityBlockedSlotResult> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/facility-blocked-slots', payload)
      )
      const raw = data as { conflictingBookings?: FacilityBlockedSlotConflict[] }
      return {
        blockedSlot: deserialize(data, FacilityBlockedSlot),
        conflictingBookings: Array.isArray(raw?.conflictingBookings) ? raw.conflictingBookings : [],
      }
    },
    /**
     * Active blocks overlapping the [startDate, endDate] window. `facilityId` is optional — omit
     * it to fetch every court's blocks for the branch (the court board needs all of them).
     * GET /facility-blocked-slots.
     */
    async getFacilityBlockedSlots(params: {
      facilityId?: string
      startDate: string
      endDate: string
    }): Promise<FacilityBlockedSlot[]> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data?: Record<string, unknown>[] } | Record<string, unknown>[]>(
          '/facility-blocked-slots',
          { params }
        )
      )
      const list = Array.isArray(data) ? data : data?.data
      return Array.isArray(list) ? list.map((item) => deserialize(item, FacilityBlockedSlot)) : []
    },
    /** Cancel a block (soft delete = ยกเลิกการปิด). DELETE /facility-blocked-slots/:id. */
    async deleteFacilityBlockedSlot(id: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/facility-blocked-slots/${id}`)
      )
      return !!data?.success
    },
    /* ---------------------------------------------------------------------- */
    /* Rental item catalog (section D)                                        */
    /* ---------------------------------------------------------------------- */
    /** Branch rental catalog. Pass `active: true` to list only enabled items (booking picker). */
    async getFacilityRentalItems(params?: { active?: boolean }): Promise<FacilityRentalItem[]> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data?: Record<string, unknown>[] } | Record<string, unknown>[]>(
          '/facility-rental-items',
          { params }
        )
      )
      const list = Array.isArray(data) ? data : data?.data
      return Array.isArray(list) ? list.map((item) => deserialize(item, FacilityRentalItem)) : []
    },
    async createFacilityRentalItem(
      payload: CreateFacilityRentalItemPayload
    ): Promise<FacilityRentalItem> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/facility-rental-items', payload)
      )
      return deserialize(data, FacilityRentalItem)
    },
    async updateFacilityRentalItem(
      id: string,
      payload: UpdateFacilityRentalItemPayload
    ): Promise<FacilityRentalItem> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/facility-rental-items/${id}`, payload)
      )
      return deserialize(data, FacilityRentalItem)
    },
    /** Soft-disable a rental item. DELETE /facility-rental-items/:id. */
    async deleteFacilityRentalItem(id: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/facility-rental-items/${id}`)
      )
      return !!data?.success
    },
    /**
     * Booking report workbook for a date range. `statuses` is sent as the
     * comma-separated `status` list the API expects; an empty list falls back
     * to the API default.
     */
    async downloadFacilityBookingReportWorkbook(input: {
      startDate: string
      endDate: string
      statuses: string[]
      lang: ReportLang
    }): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>('/facility-bookings/report/workbook', {
          params: {
            startDate: input.startDate,
            endDate: input.endDate,
            ...(input.statuses.length > 0 ? { status: input.statuses.join(',') } : {}),
            lang: input.lang,
          },
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
      )
      return data
    },
  }
}
